import { useRef } from "react";
import { Editor } from "@hugerte/hugerte-react";
import type { Editor as HugeRTEEditor } from "hugerte";

/**
 * Asset purpose passed to /api/agreements/images/upload. Drives where
 * the uploaded file is stored on the API side; the API rejects any
 * other value.
 */
export type DesignUploadType =
  | "header"
  | "footer"
  | "signature"
  | "top_left"
  | "top_right";

interface DocumentDesignEditorProps {
  value: string;
  onChange: (next: string) => void;
  height?: number;
  /** Determines the storage bucket prefix for uploaded images. */
  uploadType?: DesignUploadType;
  /** Optional sample variables for the Insert Variable menu. Header zones
   *  ship a small set; full agreement editors plug in the registry. */
  variableItems?: Array<{ text: string; value: string }>;
}

const DEFAULT_HEIGHT = 280;

const DEFAULT_VARIABLES = [
  { text: "Booking ID", value: "{{booking_id}}" },
  { text: "Booking date", value: "{{date}}" },
  { text: "Page number", value: "{{page_number}}" },
  { text: "Total pages", value: "{{total_pages}}" },
];

/**
 * Shared rich-HTML editor for the design zones (header, top-left
 * margin, top-right margin, bottom-left margin, signature block).
 * Slim toolbar with font + size + image, Insert Variable menu, no
 * menubar.
 */
export function DocumentDesignEditor({
  value,
  onChange,
  height = DEFAULT_HEIGHT,
  uploadType = "header",
  variableItems,
}: DocumentDesignEditorProps) {
  const items = variableItems ?? DEFAULT_VARIABLES;
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const uploadTypeRef = useRef(uploadType);
  uploadTypeRef.current = uploadType;

  return (
    <Editor
      value={value || ""}
      onEditorChange={(content) => {
        if (content !== (value || "")) onChange(content);
      }}
      init={{
        height,
        menubar: false,
        plugins: ["lists", "link", "image", "code"],
        // Sliding overflow keeps the toolbar to a single row, with a
        // chevron that exposes the rest. Without this, the font /
        // size pickers force everything past bold/italic/underline
        // onto a second row inside the cramped zone cards.
        toolbar_mode: "sliding",
        // Most-used buttons come first so they stay visible at the
        // narrowest widths: Insert Variable, image, link.  Bold /
        // italic / underline are less common in design zones (mostly
        // already styled via fonts), so they sit further right.
        // Font / size / code drop behind the chevron first.
        toolbar:
          "insertvariable image | link | " +
          "alignleft aligncenter alignright | " +
          "bold italic underline | fontfamily fontsize lineheight | code",
        // Short display names so the dropdowns aren't 200px wide. The
        // actual font stack is unchanged.
        font_family_formats:
          "Serif=Times New Roman,Times,serif;" +
          "Sans=Arial,Helvetica,sans-serif;" +
          "Georgia=Georgia,serif;" +
          "Mono=Courier New,Courier,monospace;" +
          "Garamond=Garamond,serif",
        font_size_formats: "8pt 10pt 12pt 14pt 16pt 18pt 24pt",
        // Drag-drop or file-picker uploads round-trip through the
        // existing /api/agreements/images/upload endpoint and resolve
        // to the uploaded asset's CDN URL.
        automatic_uploads: true,
        images_upload_handler: (blobInfo: {
          blob: () => Blob;
          filename: () => string;
        }) =>
          new Promise<string>((resolve, reject) => {
            const formData = new FormData();
            formData.append("file", blobInfo.blob(), blobInfo.filename());
            formData.append("type", uploadTypeRef.current);
            fetch("/api/agreements/images/upload", {
              method: "POST",
              credentials: "include",
              body: formData,
            })
              .then(async (res) => {
                if (!res.ok) {
                  const err = await res
                    .json()
                    .catch(() => ({ error: `Upload failed (${res.status})` }));
                  throw new Error(err.error ?? `Upload failed (${res.status})`);
                }
                return res.json() as Promise<{
                  storage_key: string;
                  cdn_url: string;
                }>;
              })
              .then((data) => resolve(data.cdn_url))
              .catch((err: Error) => reject(err.message));
          }),
        setup: (editor: HugeRTEEditor) => {
          editor.ui.registry.addMenuButton("insertvariable", {
            text: "Insert Variable",
            fetch: (callback) => {
              const menuItems = itemsRef.current.map(
                (v) =>
                  ({
                    type: "menuitem" as const,
                    text: v.text,
                    onAction: () => editor.insertContent(v.value),
                  }),
              );
              callback(menuItems);
            },
          });
        },
        content_style:
          "body { font-family: 'Times New Roman', Times, serif; font-size: 14px; line-height: 1.6; }",
        line_height_formats: "1 1.15 1.4 1.6 1.8 2",
        promotion: false,
        branding: false,
        license_key: "gpl",
      }}
    />
  );
}
